import { Button, Result, Space, Spin } from "@arco-design/web-react";
import { useQuery } from "@tanstack/react-query";
import { Navigate, useParams } from "react-router-dom";

import { isHttpClientError } from "../../lib/http/errors";
import {
  useFulfillmentOrderEntryRedirect,
} from "./runtime";
import { fetchFulfillmentWorkspaceEditor } from "./repo";
import { FulfillmentOrderEditorFeaturePage } from "./ui/FulfillmentOrderEditorFeaturePage";
import { FulfillmentOrderListFeaturePage } from "./ui/FulfillmentOrderListFeaturePage";

/** 确认件编辑器路由页。 */
export function FulfillmentOrderEditorPage() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const query = useQuery({
    queryKey: ["fulfillmentOrder", "workspaceRedirect", workspaceId],
    queryFn: () => fetchFulfillmentWorkspaceEditor(workspaceId as string),
    enabled: Boolean(workspaceId),
    retry: false,
    refetchOnWindowFocus: false,
  });
  if (!workspaceId) return <div role="alert">缺少确认件工作区标识</div>;
  if (query.isPending) return <Spin dot tip="正在读取确认件关联行程…" />;
  if (query.error) {
    const httpError = isHttpClientError(query.error) ? query.error : null;
    const canRetry = httpError === null || httpError.retriable;
    return (
      <Result
        status="error"
        title={canRetry ? "确认件工作区暂时无法打开" : "确认件工作区不存在或已删除"}
        subTitle={canRetry ? "网络或服务暂时不可用，请稍后重试。" : "请返回课程线路重新选择。"}
        extra={canRetry ? <Button type="primary" onClick={() => query.refetch()}>重试</Button> : null}
      />
    );
  }
  const itineraryId = query.data?.workspace.itineraryId;
  return itineraryId
    ? <Navigate replace to={`/supply-chain/fulfillment-orders/itineraries/${itineraryId}`} />
    : <div role="alert">该确认件未关联课程线路，无法使用规范入口</div>;
}

/** 以行程 ID 为规范地址的确认件工作台。 */
export function FulfillmentOrderItineraryPage() {
  const { itineraryId } = useParams<{ itineraryId: string }>();
  const query = useFulfillmentOrderEntryRedirect(itineraryId);
  if (!itineraryId) return <Result status="error" title="缺少课程线路标识" />;
  if (query.isPending) return <Spin dot tip="正在创建或打开确认件工作区…" />;
  if (query.error) return <FulfillmentOrderEntryError query={query} />;
  if (query.data) return <FulfillmentOrderEditorFeaturePage workspaceId={query.data.workspaceId} />;
  return <Spin dot tip="正在进入确认件工作区…" />;
}

/** 从课程线路进入确认件的中转页。 */
export function FulfillmentOrderEntryRedirectPage() {
  const { itineraryId } = useParams<{ itineraryId: string }>();
  if (!itineraryId) return <Result status="error" title="缺少课程线路标识" />;
  return (
    <Navigate
      replace
      to={`/supply-chain/fulfillment-orders/itineraries/${itineraryId}`}
    />
  );
}

/** 展示确认件入口的稳定错误文案。 */
function FulfillmentOrderEntryError({ query }: { query: ReturnType<typeof useFulfillmentOrderEntryRedirect> }) {
  const httpError = isHttpClientError(query.error) ? query.error : null;
  const isNotFound = httpError?.statusCode === 404;
  const canRetry = httpError === null || Boolean(httpError?.retriable);
  return (
    <Result
      status="error"
      title={isNotFound
        ? "课程线路不存在或已删除"
        : canRetry
          ? "确认件工作区暂时无法打开"
          : "当前无法进入确认件工作区"}
      subTitle={isNotFound
        ? "请返回课程线路列表检查数据，或重新选择一条线路。"
        : canRetry
          ? "网络或服务暂时不可用，请稍后重试。"
          : "当前课程线路无法创建或打开确认件，请返回后重新选择。"}
      extra={(
        <Space wrap>
          <Button href="/product-lines/course-routes">返回课程线路</Button>
          {canRetry ? <Button type="primary" onClick={() => query.refetch()}>重试</Button> : null}
        </Space>
      )}
    />
  );
}

/** 确认件列表路由页。 */
export function FulfillmentOrderListPage() {
  return <FulfillmentOrderListFeaturePage />;
}
